'use client';

import { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useStoreMap } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { getFileByIdParams } from '@nx-frontend/shared/store/feature-files';
import { getTextClassNames } from '@nx-frontend/shared/themes';
import { PortalStyleConfig, PortalTextType, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { downloadFile, prepareFileItemUrl } from '@nx-frontend/shared/utils';
import { getIconByFileType } from '../helpers';
import { useOpenFileMenu } from '../hooks/useOpenFileMenu';

export interface FileInfoPopupProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  fileId: string;
  fileOpenHost: string;
  orgRole?: string;
  userId?: number;
  openFileLocationEnabled?: boolean;
  canEditOtherUsersFile?: boolean;
  deleteDisabled?: boolean;
  renameDisabled?: boolean;
}

export function FileInfoPopup({
  themeColors,
  themeStyle,
  fileOpenHost,
  fileId,
  userId,
  openFileLocationEnabled = true,
  canEditOtherUsersFile,
  deleteDisabled,
  renameDisabled,
}: FileInfoPopupProps) {
  const item = useStoreMap(getFileByIdParams(fileId));
  const [closeModal] = useEvent([closeModalEvent]);
  const openFileMenu = useOpenFileMenu();
  const t = useTranslations('files_dashboard');

  if (!item) {
    return;
  }
  const icon = getIconByFileType(item.type);

  const handleDownloadClick = () => {
    const url = prepareFileItemUrl(item.url, fileOpenHost);
    downloadFile(url, item.filename);
  };

  const handleMenuClick = (event: MouseEvent) => {
    openFileMenu({
      item,
      userId,
      openFileLocationEnabled,
      fileOpenHost,
      themeColors,
      themeStyle,
      event,
      canEditOtherUsersFile,
      canCreateEsign: false,
      renameDisabled,
      deleteDisabled,
    });
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div className="flex w-full flex-col items-center justify-center max-h-[428px] min-w-[755px]">
        <div className="sm:flex items-center ml-auto justify-between hidden">
          {/*<div*/}
          {/*  className="flex items-center justify-between cursor-pointer text-cyan-900 hover:text-cyan-700 text-xs font-bold pr-7 py-1.5 pl-2.5 gap-2.5"*/}
          {/*  onClick={() => console.log('SHARE')}*/}
          {/*>*/}
          {/*  <Icon icon={IconsMap.ARROW_REPLY_1} className="w-3.5 h-3.5" />*/}
          {/*  Share*/}
          {/*</div>*/}
          {/*<div*/}
          {/*  className="flex items-center justify-between cursor-pointer text-cyan-900 hover:text-cyan-700 text-xs font-bold pr-7 py-1.5 pl-2.5 gap-2.5"*/}
          {/*  onClick={() => console.log('COMMENT')}*/}
          {/*>*/}
          {/*  <Icon icon={IconsMap.CIRCLE_COMMENT} className="w-3.5 h-3.5" />*/}
          {/*  Comment*/}
          {/*</div>*/}
          <div
            className={classNames(
              'flex items-center justify-between cursor-pointer p-2.5',
              getTextClassNames(themeColors, PortalTextType.PRIMARY)
            )}
            onClick={handleMenuClick}
          >
            <Icon icon={IconsMap.MORE_HORIZONTAL} className="w-3.5 h-3.5" />
          </div>
          <div
            className={classNames('flex items-center justify-between cursor-pointer p-2.5', 'text-gray-500')}
            onClick={() => closeModal()}
          >
            <Icon icon={IconsMap.CLOSE} className="w-3.5 h-3.5" />
          </div>
        </div>
        <div className="py-16 flex flex-col items-center justify-center">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-cover">
            <Icon icon={icon} className="w-full h-full" />
          </div>
          <div
            title={item.filename}
            className={classNames(
              'block max-w-xs md:max-w-sm lg:max-w-md font-bold mt-2 text-base text-center overflow-hidden whitespace-nowrap text-ellipsis',
              !themeColors && 'text-neutral-800',
              themeColors && getTextClassNames(themeColors, PortalTextType.SURFACE)
            )}
          >
            {item.filename}
          </div>
          <div
            className={classNames(
              'text-center mt-4 w-72 sm:w-full',
              !themeColors && 'text-neutral-800',
              themeColors && getTextClassNames(themeColors, PortalTextType.SURFACE)
            )}
          >
            {t('files_dashboard-unavailable_preview')}
          </div>
          <div className="text-white font-bold py-2 mt-4 h-full">
            <Button
              type={ButtonType.PRIMARY}
              size={ButtonSize.XL}
              isFull={true}
              themeColors={themeColors}
              onClick={handleDownloadClick}
            >
              <Icon icon={IconsMap.DOWNLOAD} className="w-5" />
              <span>{t('files_dashboard-download')}</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
